import {
  ClockIcon,
  CpuIcon,
  CheckCircleIcon,
  FolderIcon,
  GraduationCapIcon,
  HelpCircleIcon,
  LayersIcon,
  MapIcon,
  PencilIcon,
  RefreshCwIcon,
  SearchIcon,
  SlidersIcon,
  ThermometerIcon,
  WifiIcon,
  ZapIcon,
} from 'lucide-react'

type LucideIcon = typeof ZapIcon

export interface IconItem {
  icon: LucideIcon
  title: string
  description: string
}

export const painPoints: IconItem[] = [
  {
    icon: SearchIcon,
    title: 'Sin Manuales Filtrados',
    description:
      'Pasás horas buscando hojas de datos de fabricantes sin saber cuáles son relevantes para el aula.',
  },
  {
    icon: HelpCircleIcon,
    title: 'Preguntas sin Respuesta',
    description: 'Tus alumnos preguntan sobre tendencias 2026 y no sabés con precisión cómo responder.',
  },
  {
    icon: CheckCircleIcon,
    title: 'Sin Ejercicios Resueltos',
    description:
      'No tenés ejercicios de cálculo de lazos 4-20mA resueltos con criterios técnicos internacionales.',
  },
  {
    icon: PencilIcon,
    title: 'Evaluación que Consume Tiempo',
    description:
      'Diseñar rúbricas por competencias técnicas te consume tiempo que deberías dedicar a enseñar.',
  },
  {
    icon: RefreshCwIcon,
    title: 'Planificación desde Cero',
    description:
      'Cada semestre armás el plan de control y estrategia sin una estructura didáctica clara.',
  },
  {
    icon: CpuIcon,
    title: 'Laboratorio Desconectado',
    description: 'El laboratorio de sensores funciona sin conexión real con la teoría de automatización.',
  },
]

export interface IncludedItem {
  title: string
  description: string
}

export const includedInEveryVolume: IncludedItem[] = [
  {
    title: 'Contenido técnico verificado',
    description: 'Basado en normas ISA, IEC, IEEE e investigación industrial actual 2024-2026.',
  },
  {
    title: 'Material 100% imprimible',
    description: 'PDFs diseñados para aula, laboratorio y distribución a alumnos.',
  },
  {
    title: 'Protocolos paso a paso',
    description: 'Procedimientos de calibración y montaje con criterios exactos de decisión.',
  },
  {
    title: 'Casos de estudio documentados',
    description: 'Fallas reales en plantas de minería, petróleo y energía analizadas técnicamente.',
  },
  {
    title: 'Ejercicios con soluciones',
    description: 'Problemas de conversión, escalado de señales y sintonía PID resueltos.',
  },
  {
    title: 'Comparativas de tecnología',
    description: 'Diferencias reales entre sensores, transmisores y protocolos de múltiples marcas.',
  },
  {
    title: 'Conexión con innovación',
    description: 'IIoT y mantenimiento predictivo aplicables ya en la industria de 2026.',
  },
]

export interface Volume {
  icon: LucideIcon
  number: number
  title: string
  description: string
}

export const volumes: Volume[] = [
  {
    icon: ZapIcon,
    number: 1,
    title: 'El ADN de la Medida',
    description:
      'Fundamentos globales, señales 4-20mA, el estándar de oro y variables universales de proceso.',
  },
  {
    icon: ThermometerIcon,
    number: 2,
    title: 'Sensores Elite',
    description:
      'Selección y diagnóstico de PT100, termopares, transmisores DP, caudalímetros y nivel avanzado.',
  },
  {
    icon: SlidersIcon,
    number: 3,
    title: 'Control y Estrategia',
    description: 'Lazos PID, programación PLC en Ladder, sistemas SCADA y diseño de interfaces HMI.',
  },
  {
    icon: MapIcon,
    number: 4,
    title: 'El Mapa de la Planta',
    description:
      'Lectura e interpretación de P&ID bajo norma ISA-5.1, diagramas de lazo y matrices de instrumentación.',
  },
  {
    icon: WifiIcon,
    number: 5,
    title: 'Instrumentación 4.0',
    description:
      'IIoT, Edge Computing, MQTT, sensores inteligentes con autodiagnóstico y mantenimiento predictivo.',
  },
  {
    icon: GraduationCapIcon,
    number: 6,
    title: 'Gestión y Planificación',
    description:
      'Diseño curricular técnico, rúbricas de evaluación de laboratorio y proyectos Capstone anuales.',
  },
]

export interface Bonus {
  number: number
  title: string
  description: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'Guía de Clases Express',
    description: '12 sesiones listas para enseñar: pregunta, explicación, actividad y cierre.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/c3252fc9-c5f3-43e9-bcec-0db05d0362e1.jpg',
  },
  {
    number: 2,
    title: 'Diccionario Técnico Docente',
    description: 'Términos esenciales explicados de forma simple para explicar en el aula.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/341659f6-4c05-4e76-86ab-603d7956757d.jpg',
  },
  {
    number: 3,
    title: 'Banco de Explicaciones Simples',
    description: 'Analogías y ejemplos cotidianos para enseñar temas difíciles como PID o HART.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e6f6773b-ffe9-4c7a-92e0-131526e4ef6a.jpg',
  },
  {
    number: 4,
    title: 'Checklists de Aula y Laboratorio',
    description: 'Listas de verificación para preparar clases y prácticas de medición seguras.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/0cc251a0-dfdf-4265-bdc9-bb37cf3243ef.jpg',
  },
  {
    number: 5,
    title: 'Casos Industriales de Discusión',
    description: 'Situaciones realistas de fallas en planta para debate y resolución en grupo.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7bdcf954-2be9-4515-a022-2964df182828.jpg',
  },
]

export interface OfferRow {
  label: string
  detail: string
  value?: string
}

export const offerIncludes: OfferRow[] = [
  { label: 'Volumen 1: El ADN de la Medida', detail: 'Señales 4-20 mA y variables de proceso' },
  { label: 'Volumen 2: Sensores Elite', detail: 'PT100, termopares, DP, caudal y nivel' },
  { label: 'Volumen 3: Control y Estrategia', detail: 'PID, PLC en Ladder, SCADA y HMI' },
  { label: 'Volumen 4: El Mapa de la Planta', detail: 'P&ID bajo norma ISA-5.1 y diagramas de lazo' },
  { label: 'Volumen 5: Instrumentación 4.0', detail: 'IIoT, Edge Computing, MQTT y predictivo' },
  { label: 'Volumen 6: Gestión y Planificación', detail: 'Diseño curricular, rúbricas y Capstone' },
  { label: 'Bono 1: Guía de Clases Express', detail: '12 sesiones listas para dictar', value: '$14' },
  { label: 'Bono 2: Diccionario Técnico Docente', detail: 'Términos clave explicados simple', value: '$14' },
  { label: 'Bono 3: Banco de Explicaciones Simples', detail: 'Analogías para PID y HART', value: '$14' },
  { label: 'Bono 4: Checklists de Aula y Laboratorio', detail: 'Prácticas de medición seguras', value: '$14' },
  { label: 'Bono 5: Casos Industriales de Discusión', detail: 'Fallas reales para debatir en clase', value: '$14' },
  { label: 'Acceso de por vida', detail: 'Actualizaciones gratuitas incluidas' },
]

export interface Testimonial {
  quote: string
  initials: string
  name: string
  role: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'Tenía miedo de no tener respuestas cuando mis alumnos preguntaban sobre Edge Computing o sensores inteligentes. Con el Volumen 5 ahora explico tecnología 4.0 como si fuera especialista.',
    initials: 'RM',
    name: 'Ricardo M.',
    role: 'Docente de Automatización — Chile',
  },
  {
    quote:
      'El Volumen 4 me permitió enseñar lectura de P&ID sin depender de manuales viejos. Los símbolos bajo norma ISA-5.1 actualizados son exactamente lo que necesitaba.',
    initials: 'CG',
    name: 'Carlos G.',
    role: 'Profesor de Procesos Industriales — México',
  },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: '¿Sirve si enseño en nivel universitario?',
    answer:
      'Sí. Los volúmenes tienen profundidad técnica para ingeniería y adaptaciones para ciclo superior técnico, con investigación industrial actual 2024-2026.',
  },
  {
    question: '¿Cómo recibo el material?',
    answer:
      'Inmediatamente después del pago recibís un email con el acceso a todos los PDFs. Podés descargarlos, imprimirlos y usarlos en clase el mismo día.',
  },
  {
    question: '¿Cuánto tiempo lleva empezar a usar el Kit?',
    answer:
      'Con el Bono 1 (Guía de Clases Express) tenés 12 sesiones listas para dictar desde el día 1. El sistema completo queda integrado en una semana.',
  },
  {
    question: '¿Necesito software o equipos especiales?',
    answer:
      'No. Todo el material es PDF imprimible. Los ejercicios de PLC, SCADA y HMI están planteados para resolverse en papel o con simuladores gratuitos.',
  },
  {
    question: '¿Es real la garantía?',
    answer:
      'Sí. Tenés 7 días para revisar todo el material. Si no es lo que esperabas, escribís y recibís el 100% de tu dinero. Sin formularios ni condiciones.',
  },
  {
    question: '¿Aplica para todos los países de habla hispana?',
    answer:
      'Sí. El contenido se apoya en normas internacionales (ISA, IEC, IEEE), que son la referencia común en la industria de toda Hispanoamérica.',
  },
]

export const costOfInaction: IconItem[] = [
  {
    icon: LayersIcon,
    title: 'Sin sistema coherente',
    description: 'Enseñanza fragmentada sin conexión entre sensor, control y supervisión.',
  },
  {
    icon: WifiIcon,
    title: 'Sin actualización tecnológica',
    description: 'Tus alumnos no entienden la industria conectada que ya existe.',
  },
  {
    icon: FolderIcon,
    title: 'Sin casos documentados',
    description: 'Clases abstractas sin ejemplos de fallas de planta verificadas.',
  },
  {
    icon: ClockIcon,
    title: 'Sin tiempo libre',
    description: 'Horas de diseño de diagramas y rúbricas que restan horas de descanso.',
  },
]

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/cAWPBAqQYf9jjy6NsXTnaz/ChatGPT-Image-27-ago-2026_-01_03_46-Photoroom.webp'
